import React, { useRef } from "react";
import { NavLink } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import UserMenu from "./UserMenu";
import { useT } from "../i18n";
import { toggleAdvancedMode, useAdvancedMode } from "../utils/advancedMode";
import { useFieldworkerMode } from "../utils/fieldworkerMode";

interface NavBarProps {
  signedIn: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}

export default function NavBar({ signedIn, onSignIn, onSignOut }: NavBarProps) {
  const t = useT();
  const advanced = useAdvancedMode();
  const fieldworker = useFieldworkerMode();
  const clickInfo = useRef({ count: 0, last: 0 });
  const link = ({ isActive }: { isActive: boolean }) =>
    "inline-flex items-center gap-2 px-2 sm:px-3 py-2 text-sm rounded-md transition-colors " +
    (isActive ? "bg-pink-600 text-white shadow-sm" : "text-gray-700 hover:bg-gray-100");

  function handleLogoClick() {
    const now = Date.now();
    const info = clickInfo.current;
    if (now - info.last > 1500) info.count = 0;
    info.last = now;
    info.count += 1;
    if (info.count >= 5) {
      info.count = 0;
      const next = toggleAdvancedMode();
      window.alert(next ? t("nav.advancedOn") : t("nav.advancedOff"));
    }
  }

  return (
    <nav className="h-14 flex items-center px-2 sm:px-4 border-b bg-white sticky top-0 z-10 shadow-sm gap-1 sm:gap-2">
      <button
        type="button"
        onClick={handleLogoClick}
        className="flex items-center gap-2 sm:mr-2 shrink-0 select-none focus:outline-none"
        title="MeW"
      >
        <img
          src={`${process.env.PUBLIC_URL || ""}/assets/logo_64x64.png`}
          srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_32x32.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_64x64.png 2x, ${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 4x`}
          alt="MeW"
          className="h-8 w-8"
        />
        <span className="hidden sm:inline font-bold text-pink-700 tracking-tight">MeW</span>
      </button>
      <div className="flex gap-0.5 sm:gap-1 min-w-0">
        <NavLink to="/" end className={link} title={t("nav.field")}>
          <i className="fa-solid fa-location-crosshairs" aria-hidden="true" />
          <span className="hidden sm:inline">{t("nav.field")}</span>
        </NavLink>
        {/* Tasks is visible in every mode so fieldworkers can pick up incomplete records too. */}
        <NavLink to="/tasks" className={link} title={t("nav.tasks")}>
          <i className="fa-solid fa-clipboard-list" aria-hidden="true" />
          <span className="hidden sm:inline">{t("nav.tasks")}</span>
        </NavLink>
        {/* Fieldworkers (joined a shared project) only see Field entry + Tasks + Map. */}
        {!fieldworker && (
          <NavLink to="/sheet" className={link} title={t("nav.sheet")}>
            <i className="fa-solid fa-table-cells" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.sheet")}</span>
          </NavLink>
        )}
        {!fieldworker && (
          <NavLink to="/kml" className={link} title={t("nav.kml")}>
            <i className="fa-solid fa-map" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.kml")}</span>
          </NavLink>
        )}
        {!fieldworker && (
          <NavLink to="/email" className={link} title={t("nav.email")}>
            <i className="fa-solid fa-envelope" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.email")}</span>
          </NavLink>
        )}
        {!fieldworker && (
          <NavLink to="/members" className={link} title={t("nav.members")}>
            <i className="fa-solid fa-user-group" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.members")}</span>
          </NavLink>
        )}
        {!fieldworker && advanced && (
          <NavLink to="/form" className={link} title={t("nav.formSettings")}>
            <i className="fa-solid fa-list-check" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.formSettings")}</span>
          </NavLink>
        )}
        {!fieldworker && advanced && (
          <NavLink to="/import" className={link} title={t("nav.import")}>
            <i className="fa-solid fa-file-import" aria-hidden="true" />
            <span className="hidden sm:inline">{t("nav.import")}</span>
          </NavLink>
        )}
        <NavLink to="/map" className={link} title={t("nav.map")}>
          <i className="fa-solid fa-earth-asia" aria-hidden="true" />
          <span className="hidden sm:inline">{t("nav.map")}</span>
        </NavLink>
      </div>
      <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
        <LanguageSwitcher />
        {signedIn ? (
          <UserMenu onSignOut={onSignOut} />
        ) : (
          <button
            type="button"
            onClick={onSignIn}
            className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-2 sm:px-3 py-1.5"
            title={t("app.signInGoogle")}
          >
            <i className="fa-brands fa-google" aria-hidden="true" />
            <span className="hidden sm:inline">{t("app.signInGoogle")}</span>
          </button>
        )}
      </div>
    </nav>
  );
}
